<script>
	import { onMount } from 'svelte';
	import { push, replace } from 'svelte-spa-router';
	import fetchData from '../../api.js';
	import UserStore from '../../stores/user';
	import { activeCart, setActiveCart } from '../../stores/activeCart';
	import { loadCatalogMeta, loadFilterOptions } from '../../lib/catalog';
	import { money, imgUrl, swatch, timeAgo, MAIN_STORE } from '../../lib/format';
	import AppShell from '../../components/AppShell.svelte';
	import FilterChip from '../../components/FilterChip.svelte';
	import ProductSheet from '../../components/ProductSheet.svelte';
	import Icon from '../../components/Icon.svelte';

	export let params = {};

	const BRANDS = ['ALDO MORO', 'ELIXIR', 'VIBE'];
	const STORAGE_KEY = 'agents_products_filters_v2';
	const PAGE = 36;

	let search = '';
	let filters = { SupplierName: [], model_Season: [], model_Year: [], Type: [], model_Collection: [] };
	let options = {};
	let stores = {};
	let stockMinutes = null;
	let products = [];
	let loading = true;
	let error = '';
	let shown = PAGE;
	let sheetProduct = null;
	let toast = '';
	let requestNo = 0;
	let searchTimer;

	try {
		const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
		if (saved) {
			search = saved.search || '';
			filters = { ...filters, ...saved.filters };
		}
	} catch (e) {
		// no saved filters
	}

	const parseInventory = (inv) => {
		if (!inv) return {};
		if (typeof inv === 'object') return inv;
		try { return JSON.parse(inv) || {}; } catch (e) { return {}; }
	};

	function prepare(p) {
		const variants = (p.variants || []).map((v) => ({ ...v, inventory: parseInventory(v.inventory) }));
		let stock = 0;
		let stockMain = 0;
		const colorStock = {};
		for (const v of variants) {
			for (const [s, q] of Object.entries(v.inventory)) {
				const n = Number(q) || 0;
				stock += n;
				if (s === MAIN_STORE) stockMain += n;
				if (n > 0) colorStock[v.color || ''] = true;
			}
		}
		const prices = variants.map((v) => Number(v.wholesale_price)).filter((n) => n > 0);
		// one photo per color, colors in stock first; swiping the card goes through them
		const byColor = new Map();
		for (const img of p.colorImages || []) if (img.image_url && !byColor.has(img.color)) byColor.set(img.color, imgUrl(img.image_url));
		const colors = Object.keys(colorStock);
		let imageColors = colors.filter((c) => byColor.has(c));
		if (!imageColors.length) imageColors = [...byColor.keys()];
		const images = imageColors.map((c) => byColor.get(c));
		return {
			imageColors,
			...p,
			variants,
			stock,
			stockMain,
			colors,
			wholesale: prices.length ? Math.min(...prices) : 0,
			consumer: Math.max(0, ...variants.map((v) => Number(v.regular_price) || 0)),
			images
		};
	}

	async function load() {
		const mine = ++requestNo;
		loading = true;
		error = '';
		try {
			localStorage.setItem(STORAGE_KEY, JSON.stringify({ search, filters }));
		} catch (e) {
			// storage full or blocked
		}
		const res = await fetchData({ action: 'agents_products_search', token: $UserStore.data, q: search.trim(), filters });
		if (mine !== requestNo) return;
		if (!res) {
			error = 'לא הצלחנו לטעון מוצרים. בדוק את החיבור לאינטרנט ונסה שוב.';
			products = [];
		} else {
			// what can be sold from the main warehouse first, newest models first
			products = (res.data || [])
				.map(prepare)
				.filter((p) => p.stock > 0)
				.sort((a, b) => (b.stockMain > 0) - (a.stockMain > 0) || Number(b.product_id) - Number(a.product_id));
		}
		shown = PAGE;
		loading = false;
	}

	function onSearchInput() {
		clearTimeout(searchTimer);
		searchTimer = setTimeout(load, 400);
	}

	// The search must use the choice just made: with bind:selected the parent's filters were updated only
	// after the change event, so the search went out with the previous choice.
	function setFilter(key, values) {
		filters = { ...filters, [key]: [...values] };
		load();
	}

	// any 1-3 brands; picking all three is the same as "הכל"
	async function toggleBrand(brand) {
		if (!brand) filters.SupplierName = [];
		else if (filters.SupplierName.includes(brand)) filters.SupplierName = filters.SupplierName.filter((b) => b !== brand);
		else filters.SupplierName = [...filters.SupplierName, brand];
		if (filters.SupplierName.length === BRANDS.length) filters.SupplierName = [];
		await refreshOptions();
		load();
	}

	// filter values follow the brands: only what is in stock for them
	async function refreshOptions() {
		options = await loadFilterOptions(filters.SupplierName);
		for (const key of ['model_Season', 'model_Year', 'Type', 'model_Collection']) {
			const allowed = options[key] || [];
			filters[key] = filters[key].filter((v) => allowed.includes(v));
		}
	}

	// swipe a card photo left/right, or tap a color dot, to see the other colors
	let imgIndex = {};
	let pickedColor = {}; // a tapped color that has no photo yet
	let sheetColor = '';
	let swiped = false;

	function pickColor(p, c) {
		const i = p.imageColors.indexOf(c);
		if (i >= 0) {
			imgIndex[p.product_id] = i;
			pickedColor[p.product_id] = '';
		} else {
			pickedColor[p.product_id] = c;
		}
	}

	// the template passes imgIndex and pickedColor so Svelte redraws when they change
	const shownColor = (p, index, picked) => picked[p.product_id] || p.imageColors[index[p.product_id] || 0] || '';

	function swipePhotos(node, product) {
		let x0 = null;
		let y0 = null;
		const down = (e) => { x0 = e.clientX; y0 = e.clientY; };
		const up = (e) => {
			if (x0 === null) return;
			const dx = e.clientX - x0;
			const dy = e.clientY - y0;
			x0 = null;
			if (product.images.length > 1 && Math.abs(dx) > 30 && Math.abs(dx) > Math.abs(dy)) {
				swiped = true;
				const n = product.images.length;
				const i = imgIndex[product.product_id] || 0;
				imgIndex[product.product_id] = (i + (dx < 0 ? 1 : -1) + n) % n;
				pickedColor[product.product_id] = '';
			}
		};
		const cancel = () => (x0 = null);
		node.addEventListener('pointerdown', down);
		node.addEventListener('pointerup', up);
		node.addEventListener('pointercancel', cancel);
		return {
			update: (p) => (product = p),
			destroy() {
				node.removeEventListener('pointerdown', down);
				node.removeEventListener('pointerup', up);
				node.removeEventListener('pointercancel', cancel);
			}
		};
	}

	function openProduct(p) {
		if (swiped) {
			swiped = false;
			return;
		}
		// the size window opens on the color shown on the card
		sheetColor = shownColor(p, imgIndex, pickedColor);
		sheetProduct = p;
	}

	function clearAll() {
		search = '';
		filters = { SupplierName: [], model_Season: [], model_Year: [], Type: [], model_Collection: [] };
		load();
	}

	$: inCart = (($activeCart && $activeCart.items) || []).reduce((m, i) => {
		m[i.parent_id] = (m[i.parent_id] || 0) + (Number(i.quantity) || 0);
		return m;
	}, {});
	$: anyFilter = search || Object.values(filters).some((v) => v.length);

	function showToast(text) {
		toast = text;
		setTimeout(() => (toast = ''), 2200);
	}

	function updateProduct(updated) {
		products = products.map((p) => (p.product_id === updated.product_id ? prepare(updated) : p));
	}

	// load the next page of cards when the bottom of the list comes into view
	function revealMore(node) {
		const io = new IntersectionObserver((entries) => {
			if (entries[0].isIntersecting && shown < products.length) shown += PAGE;
		}, { rootMargin: '600px' });
		io.observe(node);
		return { destroy: () => io.disconnect() };
	}

	onMount(async () => {
		if (!$UserStore || !$UserStore.data) {
			push('/login');
			return;
		}
		// old links like #/agents/products/57 open that cart
		if (params && params.cart_id) {
			setActiveCart(params.cart_id);
			replace('/agents/products');
		}
		load();
		const meta = await loadCatalogMeta();
		stores = meta.stores;
		stockMinutes = meta.stockMinutesAgo;
		await refreshOptions();
	});
</script>

<AppShell>
	<label class="search" slot="top">
		<span class="icon"><Icon name="search" size={22} /></span>
		<input type="search" placeholder="חיפוש לפי שם דגם או מק״ט" bind:value={search} on:input={onSearchInput} enterkeyhint="search" />
		{#if search}<button class="clear" on:click={() => { search = ''; load(); }} aria-label="ניקוי חיפוש"><Icon name="close" size={18} /></button>{/if}
	</label>

	<div class="filters">
		<div class="seg">
			<button class:on={!filters.SupplierName.length} on:click={() => toggleBrand('')}>הכל</button>
			{#each BRANDS as b}
				<button class:on={filters.SupplierName.includes(b)} on:click={() => toggleBrand(b)}>{b}</button>
			{/each}
		</div>
		<FilterChip label="עונה" options={options.model_Season || []} selected={filters.model_Season} on:change={(e) => setFilter('model_Season', e.detail)} />
		<FilterChip label="שנה" options={options.model_Year || []} selected={filters.model_Year} on:change={(e) => setFilter('model_Year', e.detail)} />
		<FilterChip label="סוג" options={options.Type || []} selected={filters.Type} on:change={(e) => setFilter('Type', e.detail)} />
		<FilterChip label="קולקציה" options={options.model_Collection || []} selected={filters.model_Collection} on:change={(e) => setFilter('model_Collection', e.detail)} />
		{#if anyFilter}<button class="link-btn" on:click={clearAll}>ניקוי הכל</button>{/if}
	</div>

	<div class="meta">
		<span>
			{#if !loading}<strong>{products.length === 1 ? 'דגם אחד' : products.length + ' דגמים'}</strong> במלאי{/if}
		</span>
		{#if stockMinutes !== null && stockMinutes !== undefined}<span class="sync">מלאי עודכן {timeAgo(stockMinutes)}</span>{/if}
	</div>

	{#if error}
		<div class="alert-error">{error} <button class="link-btn" on:click={load}>נסה שוב</button></div>
	{:else if loading && !products.length}
		<div class="spinner" />
	{:else if !products.length}
		<div class="empty-state"><b>לא נמצאו דגמים במלאי</b>נסה חיפוש אחר או נקה את המסננים.</div>
	{:else}
		<div class="grid" class:dim={loading}>
			{#each products.slice(0, shown) as p (p.product_id)}
				<button class="card" on:click={() => openProduct(p)}>
					<div class="photo" use:swipePhotos={p}>
						{#if pickedColor[p.product_id]}
							<span class="ph">{pickedColor[p.product_id]}<small>אין עדיין תמונה לצבע הזה</small></span>
						{:else if p.images.length}
							<img src={p.images[imgIndex[p.product_id] || 0]} alt="" loading="lazy" draggable="false" />
						{:else}
							<span class="ph">{p.vendor || ''}</span>
						{/if}
						{#if p.images.length > 1 && !pickedColor[p.product_id]}
							<span class="dots">{#each p.images as _, k}<i class:on={k === (imgIndex[p.product_id] || 0)} />{/each}</span>
						{/if}
						{#if p.stockMain > 0}
							<span class={p.stockMain < 10 ? 'badge-warn stock' : 'badge-ok stock'}>{p.stockMain < 10 ? 'נשארו ' + p.stockMain : 'במלאי ' + p.stockMain}</span>
						{:else}
							<span class="badge-warn stock">בסניפים בלבד</span>
						{/if}
						{#if inCart[p.product_id]}<span class="badge-dark incart">בסל · {inCart[p.product_id]}</span>{/if}
					</div>
					<div class="body">
						<span class="tag">{p.vendor || ''}{p.type ? ' · ' + p.type : ''}</span>
						<span class="name">{(p.name || '').trim()}</span>
						<span class="muted sku">{p.sku}</span>
						<div class="prices">
							{#if p.consumer}<div class="price-line consumer"><span>צרכן</span><span>{money(p.consumer)}</span></div>{/if}
							{#if p.wholesale}
								<div class="price-line wholesale"><span>סיטונאי</span><span class="price">{money(p.wholesale)}</span></div>
							{:else}
								<span class="muted no-price">אין מחיר סיטונאי</span>
							{/if}
						</div>
						<div class="swatches">
							{#each p.colors.slice(0, 8) as c}
								<span class="sw-hit" class:on={shownColor(p, imgIndex, pickedColor) === c} role="button" tabindex="0" title={c}
									on:click|stopPropagation={() => pickColor(p, c)}
									on:keydown|stopPropagation={(e) => e.key === 'Enter' && pickColor(p, c)}>
									<span class="sw" style="background:{swatch(c)}" />
								</span>
							{/each}
							{#if p.colors.length > 8}<span class="muted more">+{p.colors.length - 8}</span>{/if}
						</div>
					</div>
				</button>
			{/each}
		</div>
		{#if shown < products.length}<div use:revealMore class="spinner" />{/if}
	{/if}
</AppShell>

{#if sheetProduct}
	<ProductSheet
		product={sheetProduct}
		initialColor={sheetColor}
		{stores}
		cart={$activeCart}
		on:close={() => (sheetProduct = null)}
		on:product={(e) => updateProduct(e.detail)}
		on:saved={() => { sheetProduct = null; showToast('הסל עודכן'); }} />
{/if}

{#if toast}<div class="toast">{toast}</div>{/if}

<style>
	.filters { display: flex; flex-wrap: wrap; align-items: center; gap: 10px; }
	.meta { display: flex; justify-content: space-between; align-items: center; gap: 12px; margin: 14px 2px 16px; color: var(--muted); font-size: 15px; }
	.meta strong { color: var(--ink); }
	.sync { display: inline-flex; align-items: center; gap: 6px; white-space: nowrap; }
	.sync::before { content: ''; width: 8px; height: 8px; border-radius: 50%; background: var(--ok); }

	.grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 18px; transition: opacity .2s; }
	.grid.dim { opacity: .5; }
	.card {
		background: var(--surface); border: 1px solid var(--line); border-radius: var(--radius);
		overflow: hidden; text-align: start; padding: 0; display: flex; flex-direction: column;
		transition: transform .12s ease, box-shadow .12s ease;
	}
	.card:active { transform: scale(.985); }
	.photo { position: relative; aspect-ratio: 3 / 4; background: var(--photo); touch-action: pan-y; user-select: none; }
	.photo img { width: 100%; height: 100%; object-fit: cover; display: block; pointer-events: none; }
	.dots { position: absolute; bottom: 10px; left: 0; right: 0; display: flex; justify-content: center; gap: 6px; }
	.dots i { width: 8px; height: 8px; border-radius: 50%; background: rgba(255, 255, 255, .6); box-shadow: 0 0 0 1px rgba(0, 0, 0, .15); }
	.dots i.on { background: #fff; width: 20px; border-radius: 4px; }
	.ph { display: grid; place-items: center; height: 100%; color: var(--muted); font-weight: 800; letter-spacing: .08em; }
	.stock { position: absolute; top: 10px; right: 10px; }
	.incart { position: absolute; top: 10px; left: 10px; }
	.body { padding: 12px 14px 14px; display: flex; flex-direction: column; gap: 3px; }
	.tag { font-size: 12px; letter-spacing: .06em; color: var(--muted); font-weight: 700; }
	.name { font-size: 17px; font-weight: 700; line-height: 1.25; }
	.sku { font-size: 14px; }
	/* consumer price, and the wholesale price under it */
	.prices { display: flex; flex-direction: column; gap: 2px; margin-top: 6px; }
	.price-line { display: flex; align-items: baseline; justify-content: space-between; gap: 8px; }
	.price-line.consumer { font-size: 15px; color: var(--muted); }
	.price-line.wholesale { font-size: 15px; font-weight: 700; }
	.price { font-size: 21px; font-weight: 800; }
	.no-price { font-size: 14px; }
	.swatches { display: flex; gap: 2px; margin-top: 6px; margin-inline-start: -7px; align-items: center; flex-wrap: wrap; }
	/* a finger-sized target around each color dot */
	.sw-hit { width: 36px; height: 36px; display: grid; place-items: center; border-radius: 50%; cursor: pointer; }
	.sw-hit .sw { width: 22px; height: 22px; }
	.sw-hit.on .sw { box-shadow: 0 0 0 2px var(--surface), 0 0 0 4px var(--ink); }
	.ph small { display: block; font-size: 13px; font-weight: 500; letter-spacing: 0; margin-top: 6px; text-align: center; }
	.more { font-size: 13px; }

	.toast {
		position: fixed; z-index: 50; bottom: calc(var(--tabbar-h) + 20px); left: 50%; transform: translateX(-50%);
		background: var(--ink); color: #fff; padding: 14px 22px; border-radius: 14px; font-weight: 700; box-shadow: var(--shadow);
	}

	@media (max-width: 1000px) {
		.grid { grid-template-columns: repeat(3, 1fr); gap: 14px; }
	}
	@media (max-width: 640px) {
		.grid { grid-template-columns: repeat(2, 1fr); }
		.meta { flex-direction: column; align-items: flex-start; }
	}
</style>
